/**
 * Custom backend adapter (uses REST API endpoints)
 */

import type { Backend, Document, NoCodeComponentEntry } from "@easyblocks/core";
import { EasyblocksApiError, joinUrl } from "../client";
import { getDocument, createDocument, updateDocument } from "../documents";

type CustomBackendOptions = {
  /**
   * Base URL where the custom endpoints live.
   * Example: "/api/easyblocks" (same-origin) or "https://example.com/api/easyblocks"
   */
  apiBaseUrl: string;
};

/**
 * Track in-flight writes so the editor "close" flow can await the last update before navigating/reloading.
 * This is intentionally module-scoped so it's shared across all backend instances within the same bundle.
 */
const pendingDocumentWrites = new Set<Promise<unknown>>();

let activeApiBaseUrl = "";

const createDefaultEntry = (): NoCodeComponentEntry => ({
  _component: "RootLayoutSections",
  _id: "easyblocks-root-stack",
  Sections: [],
});

const templatesDisabled: Backend["templates"] = {
  get: async (payload) => {
    throw new Error(`Templates are not supported (requested: ${payload.id})`);
  },
  getAll: async () => [],
  create: async () => {
    throw new Error("Templates are not supported");
  },
  update: async () => {
    throw new Error("Templates are not supported");
  },
  delete: async () => {
    throw new Error("Templates are not supported");
  },
};

function publishPendingWritesToWindow() {
  if (typeof window === "undefined") return;
  // Expose a tiny, stable hook for the host to decide whether it needs to wait before reloading.
  // This avoids polling when there are no in-flight saves.
  (window as any).__easyblocksPendingDocumentWritesCount = pendingDocumentWrites.size;
}

function trackWrite<T>(p: Promise<T>): Promise<T> {
  pendingDocumentWrites.add(p);
  publishPendingWritesToWindow();
  p.finally(() => pendingDocumentWrites.delete(p));
  p.finally(() => publishPendingWritesToWindow());
  return p;
}

/**
 * Wait until all currently in-flight document writes are settled.
 * Useful right before closing the editor so the host doesn't reload "too early".
 */
export async function waitForPendingDocumentWrites(options?: {
  /**
   * Maximum time to wait (best-effort).
   * Keep this short for UX-sensitive flows (like closing the editor).
   */
  timeoutMs?: number;
  /**
   * If "snapshot" (default), waits only for the writes that were in-flight at call time.
   * If "drain", keeps waiting until the set is empty (may be slower if new writes keep coming).
   */
  mode?: "snapshot" | "drain";
}): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? 10_000;
  const mode = options?.mode ?? "snapshot";

  const startedAt = Date.now();
  const initial = Array.from(pendingDocumentWrites);

  if (mode === "snapshot") {
    if (initial.length === 0) return;
    await Promise.race([
      Promise.allSettled(initial).then(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
    return;
  }

  // drain mode
  while (pendingDocumentWrites.size > 0) {
    const snapshot = Array.from(pendingDocumentWrites);
    await Promise.race([
      Promise.allSettled(snapshot).then(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, 50)),
    ]);

    if (Date.now() - startedAt > timeoutMs) return;
  }
}

/**
 * Backend adapter for the custom endpoints:
 * - GET  /api/easyblocks/documents?id=...&locale=en-US
 * - POST /api/easyblocks/documents   body: { id, version, entry }
 */
export function createCustomBackend(options: CustomBackendOptions): Backend {
  const apiBaseUrl = options.apiBaseUrl;
  activeApiBaseUrl = apiBaseUrl;

  return {
    documents: {
      get: async (payload: { id: string; locale?: string }): Promise<Document> => {
        // Documents must exist before opening the editor (created when saving KD_page with use_easyblocks=Y)
        return await getDocument({
          id: payload.id,
          locale: payload.locale,
        });
      },

      /**
       * MVP note: your plan does not define a create endpoint.
       * Easyblocks can work without it as long as the editor is always opened with `?document=...`.
       */
      create: async (payload: Omit<Document, "id" | "version">): Promise<Document> => {
        const id = `doc:${Date.now()}`;

        // Best-effort: try to persist via the update endpoint (requires server to support upsert or "create on demand").
        // If the server rejects it, we still return a client-generated document for local use.
        try {
          return await trackWrite(
            createDocument({
              id,
              version: 0,
              entry: payload.entry as NoCodeComponentEntry,
            })
          );
        } catch {
          return { id, version: 1, entry: payload.entry as NoCodeComponentEntry };
        }
      },

      update: async (payload: Omit<Document, "type">): Promise<Document> => {
        try {
          return await trackWrite(
            updateDocument({
              id: payload.id,
              version: payload.version,
              entry: payload.entry,
              record_revision: (payload as any).record_revision,
            })
          );
        } catch (e) {
          // Preserve 409 for optimistic locking.
          if (e instanceof EasyblocksApiError && e.status === 409) {
            throw e;
          }
          throw e;
        }
      },
    },

    templates: templatesDisabled,
  };
}
